import {clsx} from 'clsx'
import {pullRequestProcessingIndicatorPath} from '@github-ui/paths'
import {useGetHovercardAttributesForType} from '@github-ui/hovercards/use-get-hovercard-attributes-for-type'
import {ProfileReference} from '@github-ui/profile-reference'
import {GitHubAvatar} from '@github-ui/github-avatar'
import {isCopilot} from '@github-ui/assignees/copilot-user'
import {Link, RelativeTime} from '@primer/react'

import {isFeatureEnabled} from '@github-ui/feature-flags'
import {type PullRequestState as PullRequestStateType, PullRequestState} from '../types'
import styles from './PullRequestHeaderSummary.module.css'
import {PullRequestsProcessingIndicatorWithDataFetching} from '@github-ui/pull-requests-processing-indicator/PullRequestsProcessingIndicator'
import {isEnterprise} from '@github-ui/runtime-environment'
import {DiffStat} from './DiffStat'
import {PullRequestHeaderAuthors} from './PullRequestHeaderAuthors'
import {PullRequestHeaderBranches, type PullRequestHeaderBranchesProps} from './PullRequestHeaderBranches'
import {useIsOverviewEnabled} from '../hooks/use-feature-state'

export interface PullRequestHeaderSummaryProps extends PullRequestHeaderBranchesProps {
  author: {
    login: string
    displayName?: string | null
    avatarUrl?: string | null
    isAgent?: boolean
    slug?: string | null
  }
  attributedUser?: {
    login: string
    displayName?: string | null
    avatarUrl?: string | null
  } | null
  className?: string
  commitsCount: number
  isInAdvisoryRepo?: boolean
  isEditing?: boolean
  mergedBy?: string | null
  mergedByAvatarUrl?: string | null
  mergedByName?: string | null
  mergedTime?: string
  pullRequestId: number
  pullRequestNumber: number
  repositoryId: number
  setIsEditing?: (isEditing: boolean) => void
  state: PullRequestStateType
}

export function PullRequestHeaderSummary({
  author,
  attributedUser,
  baseBranch,
  baseRepositoryDefaultBranch = '',
  baseRepositoryName = '',
  baseRepositoryOwnerLogin = '',
  canChangeBase = false,
  className,
  commitsCount,
  headBranch,
  headRepositoryOwnerLogin = '',
  headRepositoryName = '',
  isInAdvisoryRepo,
  isEditing = false,
  mergedBy,
  mergedByAvatarUrl,
  mergedByName,
  mergedTime,
  pullRequestId,
  pullRequestNumber,
  repositoryId,
  setIsEditing,
  state,
  stack,
}: PullRequestHeaderSummaryProps) {
  const getHovercardAttributesForType = useGetHovercardAttributesForType()

  const summaryActor =
    state === PullRequestState.Merged
      ? mergedBy
        ? {login: mergedBy, displayName: mergedByName, avatarUrl: mergedByAvatarUrl}
        : null
      : author
  const messageText = getSummaryMessageText(state, commitsCount, summaryActor?.login)
  const showUpdatedHeader = useIsOverviewEnabled()

  // Show Copilot alongside the human it is attributed to when the PR author is an agent.
  // Only applies while the author (not a merger) is the summary actor.
  const authorIsAgent = author.isAgent ?? isCopilot(author.login)
  const showCoAttribution =
    isFeatureEnabled('pull_request_copilot_attribution_header') &&
    summaryActor === author &&
    authorIsAgent &&
    !!attributedUser

  return (
    <>
      <div
        className={clsx(
          'fgColor-muted d-flex flex-items-center overflow-hidden',
          styles.summaryContainer,
          showUpdatedHeader && 'gap-2',
          className,
        )}
      >
        {showCoAttribution && attributedUser ? (
          <>
            <PullRequestHeaderAuthors
              agent={author}
              attributedUser={attributedUser}
              showAvatars={showUpdatedHeader}
            />{' '}
          </>
        ) : summaryActor ? (
          <>
            <Link
              muted
              inline={!showUpdatedHeader}
              className={clsx('text-bold', showUpdatedHeader && 'd-inline-flex flex-items-center gap-2')}
              {...getHovercardAttributesForType('user', {login: summaryActor.login})}
              href={`/${summaryActor.login}`}
            >
              {showUpdatedHeader && summaryActor.avatarUrl && (
                <GitHubAvatar src={summaryActor.avatarUrl} alt="" size={20} />
              )}
              <ProfileReference login={summaryActor.login} profileName={summaryActor.displayName} isAgent={false} />
            </Link>{' '}
          </>
        ) : null}
        {/* eslint-disable-next-line react-google-translate/no-conditional-text-nodes-with-siblings */}
        {showUpdatedHeader ? null : messageText}
        <PullRequestHeaderBranches
          baseBranch={baseBranch}
          baseRepositoryDefaultBranch={baseRepositoryDefaultBranch}
          baseRepositoryOwnerLogin={baseRepositoryOwnerLogin}
          baseRepositoryName={baseRepositoryName}
          canChangeBase={canChangeBase}
          headBranch={headBranch}
          headRepositoryOwnerLogin={headRepositoryOwnerLogin}
          headRepositoryName={headRepositoryName}
          isInAdvisoryRepo={isInAdvisoryRepo}
          isEditing={isEditing}
          setIsEditing={setIsEditing}
          stack={stack}
        />
        {showUpdatedHeader && <DiffStat />}
        {state === PullRequestState.Merged && !showUpdatedHeader && <RelativeTime datetime={mergedTime} />}
        {state === PullRequestState.Open && !isEnterprise() && (
          <PullRequestsProcessingIndicatorWithDataFetching
            processingIndicatorUrl={pullRequestProcessingIndicatorPath({
              owner: baseRepositoryOwnerLogin,
              repo: baseRepositoryName,
              number: pullRequestNumber,
            })}
            repositoryId={repositoryId}
            pullRequestId={pullRequestId}
          />
        )}
      </div>
    </>
  )
}

function getSummaryMessageText(state: PullRequestState, commitsCount: number, actor?: string) {
  const commitsNumberText = `${commitsCount} ${commitsCount > 1 ? 'commits' : 'commit'}`

  if (state === PullRequestState.Merged) {
    if (actor) {
      return `merged ${commitsNumberText} into`
    } else {
      return `${commitsNumberText} merged into`
    }
  } else {
    return `wants to merge ${commitsNumberText} into`
  }
}
